import { PythonTopicExercise } from '../../../../types/python-topic';

export const estructurasFuncionesExercises: PythonTopicExercise[] = [
  {
    id: 1,
    title: 'Funciones básicas',
    description: 'Aprende a crear y usar funciones en Python: definición, parámetros y return.',
    initialCode: `# Crea una función que suma dos números
def suma(a, b):
    # Completa la función para que devuelva la suma
    return 

# Llama la función con diferentes valores
resultado1 = suma(5, 3)
resultado2 = suma(10, 20)

print("Suma de 5 y 3:", resultado1)
print("Suma de 10 y 20:", resultado2)`,
    solution: `# Crea una función que suma dos números
def suma(a, b):
    # Completa la función para que devuelva la suma
    return a + b

# Llama la función con diferentes valores
resultado1 = suma(5, 3)
resultado2 = suma(10, 20)

print("Suma de 5 y 3:", resultado1)
print("Suma de 10 y 20:", resultado2)`,
    testCases: [],
    hints: [
      'def define una función',
      'return devuelve un valor',
      'a y b son parámetros',
      'Puedes llamar la función múltiples veces'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué aprender funciones?',
      content: `Las **funciones** son bloques de código reutilizables que realizan una tarea específica. Te permiten organizar tu código, evitar repetición, y hacer tu programa más mantenible.

### ¿Por qué son esenciales?
- **Reutilización**: Escribe código una vez, úsalo muchas veces
- **Organización**: Separa tu código en unidades lógicas
- **Mantenibilidad**: Cambios en un solo lugar afectan todo el código
- **Testabilidad**: Fácil probar funciones individualmente

### En el mundo real:
Cuando un desarrollador necesita calcular totales en múltiples partes de su aplicación (carrito, checkout, reportes), crea una función reutilizable en lugar de repetir código.`,
      keyPoints: [
        'def define una función',
        'Los parámetros son variables que recibe la función',
        'return devuelve un valor al código que llama',
        'Puedes llamar la función múltiples veces con diferentes valores'
      ],
      realWorldExample: 'En un sistema de facturación, una función calcular_total() se usa en múltiples lugares: carrito, checkout, reportes. Sin funciones, tendrías que repetir el código en cada lugar.'
    },
    theory: `# Funciones en Python

## ¿Qué es una Función?

Una **función** es un bloque de código reutilizable que realiza una tarea específica. Puede recibir datos (parámetros) y devolver un resultado.

## Sintaxis Básica

\`\`\`python
def nombre_funcion(parametro1, parametro2):
    # Código de la función
    return resultado
\`\`\`

## Componentes de una Función

### 1. Definición (def)
\`\`\`python
def suma(a, b):
\`\`\`
- \`def\`: Palabra clave que define una función
- \`suma\`: Nombre de la función
- \`(a, b)\`: Parámetros que recibe la función

### 2. Cuerpo de la Función
\`\`\`python
    return a + b
\`\`\`
- El código indentado es el cuerpo de la función
- \`return\`: Devuelve un valor

### 3. Llamar la Función
\`\`\`python
resultado = suma(5, 3)
\`\`\`
- Llama la función con argumentos
- El resultado se asigna a una variable

## Ejemplo Completo

\`\`\`python
def suma(a, b):
    return a + b

resultado = suma(5, 3)
print(resultado)  # 8
\`\`\`

## Ventajas de las Funciones

- **Reutilización**: Escribe una vez, usa muchas veces
- **Organización**: Código más estructurado
- **Mantenimiento**: Cambios en un solo lugar
- **Testeo**: Fácil probar con diferentes valores

## Parámetros y Argumentos

- **Parámetros**: Variables en la definición (\`a, b\`)
- **Argumentos**: Valores que pasas al llamar (\`5, 3\`)

\`\`\`python
def suma(a, b):  # a y b son parámetros
    return a + b

suma(5, 3)  # 5 y 3 son argumentos
\`\`\`

## Return

\`return\` termina la función y devuelve un valor:

\`\`\`python
def suma(a, b):
    return a + b  # Devuelve el resultado

resultado = suma(5, 3)  # resultado = 8
\`\`\`

Sin \`return\`, la función devuelve \`None\`.`,
    tips: [
      'def define una función, return devuelve un valor',
      'Los parámetros son variables locales a la función',
      'Puedes llamar la función múltiples veces con diferentes argumentos',
      'El nombre de la función debe ser descriptivo',
      'La indentación define el cuerpo de la función'
    ],
    advice: [
      'Practica creando funciones simples antes de funciones complejas',
      'Usa nombres descriptivos para funciones y parámetros',
      'Una función debe hacer una cosa bien',
      'Piensa en qué valores necesita recibir y qué debe devolver'
    ]
  },
  {
    id: 2,
    title: 'Funciones con múltiples parámetros',
    description: 'Crea funciones que reciben múltiples parámetros y realizan cálculos más complejos.',
    initialCode: `# Crea una función que calcula el área de un rectángulo
def area_rectangulo(base, altura):
    # Calcula y devuelve el área
    return 

# Crea una función que calcula el promedio de tres números
def promedio(a, b, c):
    # Calcula y devuelve el promedio
    return 

# Prueba las funciones
area = area_rectangulo(10, 5)
prom = promedio(85, 90, 78)

print("Área del rectángulo:", area)
print("Promedio:", prom)`,
    solution: `# Crea una función que calcula el área de un rectángulo
def area_rectangulo(base, altura):
    # Calcula y devuelve el área
    return base * altura

# Crea una función que calcula el promedio de tres números
def promedio(a, b, c):
    # Calcula y devuelve el promedio
    return (a + b + c) / 3

# Prueba las funciones
area = area_rectangulo(10, 5)
prom = promedio(85, 90, 78)

print("Área del rectángulo:", area)
print("Promedio:", prom)`,
    testCases: [],
    hints: [
      'Puedes tener múltiples parámetros separados por comas',
      'Usa paréntesis para agrupar operaciones',
      'Cada parámetro es una variable dentro de la función',
      'Puedes usar todos los parámetros en el return'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué funciones con múltiples parámetros?',
      content: `Muchas funciones necesitan múltiples valores para realizar su tarea. Aprender a trabajar con múltiples parámetros te permite crear funciones más útiles y versátiles.

### ¿Por qué este ejercicio?
- **Funciones reales**: La mayoría de funciones necesitan múltiples datos
- **Flexibilidad**: Puedes pasar diferentes combinaciones de valores
- **Aplicación práctica**: Cálculos geométricos y estadísticos son comunes

### En el mundo real:
Cuando un sistema calcula el precio total de una compra, necesita cantidad (int) y precio_unitario (float). Una función con múltiples parámetros maneja esto perfectamente.`,
      keyPoints: [
        'Puedes tener tantos parámetros como necesites',
        'Los parámetros se separan por comas',
        'Cada parámetro es una variable dentro de la función',
        'Puedes usar todos los parámetros en el cálculo'
      ],
      realWorldExample: 'En un sistema de geometría, una función calcular_volumen() necesita largo, ancho y alto. Sin múltiples parámetros, esto sería imposible.'
    },
    theory: `# Funciones con Múltiples Parámetros

## Múltiples Parámetros

Las funciones pueden recibir múltiples parámetros:

\`\`\`python
def area_rectangulo(base, altura):
    return base * altura
\`\`\`

## Orden de Parámetros

El orden importa cuando llamas la función:

\`\`\`python
# Correcto: base=10, altura=5
area = area_rectangulo(10, 5)

# Incorrecto: base=5, altura=10 (resultado diferente)
area = area_rectangulo(5, 10)
\`\`\`

## Ejemplo: Promedio

\`\`\`python
def promedio(a, b, c):
    return (a + b + c) / 3

prom = promedio(85, 90, 78)  # 84.33
\`\`\`

## Usar Todos los Parámetros

Puedes usar todos los parámetros en el cálculo:

\`\`\`python
def calcular_total(cantidad, precio, descuento):
    subtotal = cantidad * precio
    total = subtotal * (1 - descuento)
    return total
\`\`\`

## Ventajas

- **Flexibilidad**: Funciones que manejan múltiples valores
- **Claridad**: Cada parámetro tiene un propósito claro
- **Reutilización**: Misma función, diferentes valores`,
    tips: [
      'Separa múltiples parámetros con comas',
      'El orden de los parámetros importa al llamar la función',
      'Usa nombres descriptivos para cada parámetro',
      'Puedes usar todos los parámetros en el return',
      'Cada parámetro es una variable dentro de la función'
    ],
    advice: [
      'Piensa en qué datos necesita tu función para funcionar',
      'Usa nombres descriptivos para los parámetros',
      'Mantén el número de parámetros razonable (3-4 máximo)',
      'Si necesitas más parámetros, considera usar estructuras de datos'
    ]
  },
  {
    id: 3,
    title: 'Funciones sin return',
    description: 'Aprende que las funciones pueden no devolver valores, solo realizar acciones.',
    initialCode: `# Crea una función que imprime un saludo
def saludar(nombre):
    # Imprime un saludo personalizado
    print()

# Crea una función que muestra información de un producto
def mostrar_producto(nombre, precio):
    # Muestra el nombre y precio del producto
    print()
    print()

# Llama las funciones
saludar("María")
mostrar_producto("Laptop", 999.99)`,
    solution: `# Crea una función que imprime un saludo
def saludar(nombre):
    # Imprime un saludo personalizado
    print(f"¡Hola, {nombre}!")

# Crea una función que muestra información de un producto
def mostrar_producto(nombre, precio):
    # Muestra el nombre y precio del producto
    print(f"Producto: {nombre}")
    print(f"Precio: $\\{precio}")

# Llama las funciones
saludar("María")
mostrar_producto("Laptop", 999.99)`,
    testCases: [],
    hints: [
      'No todas las funciones necesitan return',
      'Algunas funciones solo realizan acciones (como imprimir)',
      'Si no hay return, la función devuelve None',
      'Puedes usar f-strings para formatear texto'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué funciones sin return?',
      content: `No todas las funciones necesitan devolver un valor. Algunas funciones solo realizan acciones como imprimir, modificar variables globales, o escribir archivos.

### ¿Por qué este ejercicio?
- **Diferentes propósitos**: No todas las funciones calculan valores
- **Acciones vs Cálculos**: Algunas funciones hacen cosas, otras devuelven cosas
- **Aplicación práctica**: Funciones de presentación, logging, etc.

### En el mundo real:
Cuando un sistema muestra un menú, guarda un log, o actualiza una interfaz, usa funciones que no devuelven valores. Su propósito es realizar una acción, no calcular algo.`,
      keyPoints: [
        'Las funciones pueden no tener return',
        'Si no hay return, la función devuelve None',
        'Algunas funciones solo realizan acciones',
        'No todas las funciones necesitan devolver valores'
      ],
      realWorldExample: 'En un sistema de logging, una función registrar_evento() solo escribe en un archivo. No necesita devolver nada, solo realizar la acción.'
    },
    theory: `# Funciones sin Return

## Funciones que No Devuelven Valores

No todas las funciones necesitan devolver un valor. Algunas solo realizan acciones:

\`\`\`python
def saludar(nombre):
    print(f"¡Hola, {nombre}!")
\`\`\`

## Return Implícito

Si una función no tiene \`return\`, devuelve \`None\` automáticamente:

\`\`\`python
def saludar(nombre):
    print(f"¡Hola, {nombre}!")

resultado = saludar("Juan")
print(resultado)  # None
\`\`\`

## Cuándo No Usar Return

Usa funciones sin return para:
- **Mostrar información**: print(), mostrar menús
- **Modificar estado**: Actualizar variables globales
- **Efectos secundarios**: Escribir archivos, enviar emails
- **Acciones**: Guardar datos, actualizar interfaz

## Ejemplo: Función de Presentación

\`\`\`python
def mostrar_producto(nombre, precio):
    print(f"Producto: {nombre}")
    print(f"Precio: $\\{precio}")
    # No necesita return, solo muestra información
\`\`\`

## Funciones con y sin Return

### Con return (calcula):
\`\`\`python
def calcular_total(precio, cantidad):
    return precio * cantidad
\`\`\`

### Sin return (muestra):
\`\`\`python
def mostrar_total(total):
    print(f"Total: $\\{total}")
\`\`\`

## Combinar Ambos Tipos

\`\`\`python
# Calcula
total = calcular_total(10, 5)

# Muestra
mostrar_total(total)
\`\`\``,
    tips: [
      'No todas las funciones necesitan return',
      'Si no hay return, la función devuelve None',
      'Algunas funciones solo realizan acciones',
      'Usa funciones sin return para mostrar información',
      'Puedes combinar funciones con y sin return'
    ],
    advice: [
      'Piensa si tu función necesita devolver algo o solo hacer algo',
      'Funciones que calculan: usan return',
      'Funciones que muestran: no necesitan return',
      'No mezcles ambos propósitos en una función'
    ]
  },
  {
    id: 4,
    title: 'Funciones con valores por defecto',
    description: 'Aprende a crear funciones con parámetros opcionales usando valores por defecto.',
    initialCode: `# Crea una función con parámetro opcional
def saludar(nombre, mensaje="Hola"):
    # Usa el mensaje por defecto o el proporcionado
    print()

# Prueba con y sin el parámetro opcional
saludar("Juan")
saludar("María", "Buenos días")`,
    solution: `# Crea una función con parámetro opcional
def saludar(nombre, mensaje="Hola"):
    # Usa el mensaje por defecto o el proporcionado
    print(f"{mensaje}, {nombre}!")

# Prueba con y sin el parámetro opcional
saludar("Juan")
saludar("María", "Buenos días")`,
    testCases: [],
    hints: [
      'Los parámetros con valor por defecto van al final',
      'Si no proporcionas el valor, se usa el por defecto',
      'Puedes tener múltiples parámetros opcionales',
      'Los parámetros opcionales hacen las funciones más flexibles'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar valores por defecto?',
      content: `Los valores por defecto hacen que las funciones sean más flexibles. Permiten que algunos parámetros sean opcionales, haciendo la función más fácil de usar en casos comunes.

### ¿Por qué este ejercicio?
- **Flexibilidad**: Funciones que funcionan con o sin todos los parámetros
- **Facilidad de uso**: Valores comunes no necesitan especificarse
- **Aplicación práctica**: Muchas funciones tienen parámetros opcionales

### En el mundo real:
Cuando un sistema envía un email, tiene parámetros opcionales como formato (HTML por defecto) o prioridad (normal por defecto). Los valores por defecto hacen la función más fácil de usar.`,
      keyPoints: [
        'Los parámetros con valor por defecto son opcionales',
        'Van al final de la lista de parámetros',
        'Si no proporcionas el valor, se usa el por defecto',
        'Hacen las funciones más flexibles y fáciles de usar'
      ],
      realWorldExample: 'En un sistema de impresión, una función imprimir() tiene parámetros opcionales como color (True por defecto) y tamaño (A4 por defecto).'
    },
    theory: `# Valores por Defecto en Funciones

## Parámetros Opcionales

Puedes dar valores por defecto a los parámetros:

\`\`\`python
def saludar(nombre, mensaje="Hola"):
    print(f"{mensaje}, {nombre}!")
\`\`\`

## Cómo Funciona

### Con valor por defecto:
\`\`\`python
saludar("Juan")  # Usa "Hola" por defecto
# Imprime: Hola, Juan!
\`\`\`

### Sin valor por defecto:
\`\`\`python
saludar("María", "Buenos días")  # Usa el valor proporcionado
# Imprime: Buenos días, María!
\`\`\`

## Orden de Parámetros

Los parámetros con valor por defecto deben ir **después** de los obligatorios:

\`\`\`python
# Correcto
def funcion(obligatorio, opcional="valor"):
    pass

# Incorrecto
def funcion(opcional="valor", obligatorio):  # Error!
    pass
\`\`\`

## Múltiples Parámetros Opcionales

\`\`\`python
def crear_usuario(nombre, edad=18, activo=True):
    print(f"{nombre}, {edad} años, activo: {activo}")

crear_usuario("Juan")  # edad=18, activo=True por defecto
crear_usuario("María", 25)  # activo=True por defecto
crear_usuario("Pedro", 30, False)  # Todos especificados
\`\`\`

## Ventajas

- **Flexibilidad**: Funciona con diferentes combinaciones
- **Facilidad**: Valores comunes no necesitan especificarse
- **Mantenibilidad**: Cambiar el valor por defecto afecta todos los usos`,
    tips: [
      'Los parámetros opcionales van al final',
      'Si no proporcionas el valor, se usa el por defecto',
      'Puedes tener múltiples parámetros opcionales',
      'Los valores por defecto hacen las funciones más flexibles',
      'Puedes sobrescribir el valor por defecto al llamar la función'
    ],
    advice: [
      'Usa valores por defecto para parámetros comunes',
      'Mantén los parámetros opcionales al final',
      'Elige valores por defecto que sean los más usados',
      'Documenta qué hace cada parámetro opcional'
    ]
  },
  {
    id: 5,
    title: 'Tipos de datos avanzados',
    description: 'Aprende sobre tipos de datos más avanzados: tuplas, sets y diccionarios básicos.',
    initialCode: `# Trabaja con diferentes tipos de datos
# Tupla: colección ordenada e inmutable
coordenadas = (10, 20)

# Set: colección sin duplicados
numeros_unicos = {1, 2, 3, 4, 5}

# Diccionario: pares clave-valor
producto = {
    "nombre": "Laptop",
    "precio": 999.99,
    "stock": 15
}

# Accede a los valores
print("Coordenadas:", coordenadas)
print("Números únicos:", numeros_unicos)
print("Producto:", producto)
print("Nombre del producto:", producto["nombre"])
print("Precio:", producto["precio"])`,
    solution: `# Trabaja con diferentes tipos de datos
# Tupla: colección ordenada e inmutable
coordenadas = (10, 20)

# Set: colección sin duplicados
numeros_unicos = {1, 2, 3, 4, 5}

# Diccionario: pares clave-valor
producto = {
    "nombre": "Laptop",
    "precio": 999.99,
    "stock": 15
}

# Accede a los valores
print("Coordenadas:", coordenadas)
print("Números únicos:", numeros_unicos)
print("Producto:", producto)
print("Nombre del producto:", producto["nombre"])
print("Precio:", producto["precio"])`,
    testCases: [],
    hints: [
      'Las tuplas usan paréntesis ()',
      'Los sets usan llaves {}',
      'Los diccionarios usan llaves {} con clave:valor',
      'Los diccionarios se acceden con ["clave"]',
      'Las tuplas son inmutables (no se pueden modificar)'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué aprender tipos de datos avanzados?',
      content: `Python tiene varios tipos de datos además de listas. Cada uno tiene un propósito específico y es más eficiente para ciertas tareas.

### ¿Por qué este ejercicio?
- **Herramientas adecuadas**: Cada tipo es mejor para ciertas tareas
- **Eficiencia**: Algunos tipos son más rápidos para operaciones específicas
- **Aplicación práctica**: Necesitas diferentes tipos en aplicaciones reales

### En el mundo real:
Cuando un sistema almacena coordenadas GPS, usa tuplas (inmutables). Cuando necesita eliminar duplicados, usa sets. Cuando necesita asociar datos (nombre→valor), usa diccionarios.`,
      keyPoints: [
        'Tuplas: inmutables, ordenadas, para datos fijos',
        'Sets: sin duplicados, para verificar pertenencia',
        'Diccionarios: clave-valor, para datos estructurados',
        'Cada tipo tiene ventajas para diferentes tareas'
      ],
      realWorldExample: 'En un sistema de inventario, usas diccionarios para productos (clave: código, valor: información), sets para códigos únicos, y tuplas para coordenadas de almacén.'
    },
    theory: `# Tipos de Datos Avanzados

## Tuplas

Colecciones ordenadas e **inmutables**:

\`\`\`python
coordenadas = (10, 20)
# No se pueden modificar después de crear
\`\`\`

**Uso**: Datos que no deben cambiar (coordenadas, fechas, configuraciones)

## Sets

Colecciones **sin duplicados** y sin orden:

\`\`\`python
numeros_unicos = {1, 2, 3, 4, 5}
# Automáticamente elimina duplicados
\`\`\`

**Uso**: Eliminar duplicados, verificar pertenencia rápidamente

## Diccionarios

Pares **clave-valor**:

\`\`\`python
producto = {
    "nombre": "Laptop",
    "precio": 999.99,
    "stock": 15
}
\`\`\`

**Uso**: Datos estructurados, asociar información

## Acceso a Diccionarios

\`\`\`python
producto["nombre"]  # "Laptop"
producto["precio"]   # 999.99
\`\`\`

## Comparación de Tipos

| Tipo | Mutabilidad | Orden | Duplicados | Uso |
|------|-------------|-------|------------|-----|
| Lista | Mutable | Sí | Sí | Colecciones generales |
| Tupla | Inmutable | Sí | Sí | Datos fijos |
| Set | Mutable | No | No | Valores únicos |
| Dict | Mutable | Sí* | Claves únicas | Datos estructurados |

*Orden en Python 3.7+

## Cuándo Usar Cada Tipo

- **Lista**: Cuando necesitas modificar y ordenar
- **Tupla**: Cuando los datos no deben cambiar
- **Set**: Cuando necesitas valores únicos
- **Diccionario**: Cuando necesitas asociar claves con valores`,
    tips: [
      'Tuplas: inmutables, usan ()',
      'Sets: sin duplicados, usan {}',
      'Diccionarios: clave-valor, usan {}',
      'Los diccionarios se acceden con ["clave"]',
      'Cada tipo tiene ventajas para diferentes tareas'
    ],
    advice: [
      'Elige el tipo de dato según lo que necesites hacer',
      'Tuplas para datos que no cambian',
      'Sets para eliminar duplicados o verificar pertenencia',
      'Diccionarios para datos estructurados con claves',
      'Practica con cada tipo para entender sus ventajas'
    ]
  },
  {
    id: 6,
    title: 'Funciones con cálculos complejos',
    description: 'Crea funciones que realizan cálculos más complejos usando múltiples operaciones.',
    initialCode: `# Crea una función que calcula el área de un círculo
import math

def area_circulo(radio):
    # Calcula el área usando la fórmula: π * r²
    return 

# Crea una función que calcula el descuento total
def calcular_descuento(precio, porcentaje):
    # Calcula el precio con descuento
    # Ejemplo: precio=100, porcentaje=20 → resultado=80
    return 

# Prueba las funciones
print("Área del círculo:", area_circulo(5))
print("Precio con descuento:", calcular_descuento(100, 20))`,
    solution: `# Crea una función que calcula el área de un círculo
import math

def area_circulo(radio):
    # Calcula el área usando la fórmula: π * r²
    return math.pi * radio ** 2

# Crea una función que calcula el descuento total
def calcular_descuento(precio, porcentaje):
    # Calcula el precio con descuento
    # Ejemplo: precio=100, porcentaje=20 → resultado=80
    return precio * (1 - porcentaje / 100)

# Prueba las funciones
print("Área del círculo:", area_circulo(5))
print("Precio con descuento:", calcular_descuento(100, 20))`,
    testCases: [],
    hints: [
      'Usa math.pi para el valor de π',
      'El operador ** eleva a una potencia',
      'Para descuento: precio * (1 - porcentaje/100)',
      'Puedes usar múltiples operaciones en una función'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué funciones con cálculos complejos?',
      content: `Las funciones permiten encapsular cálculos complejos en una sola unidad reutilizable. Esto hace tu código más legible y mantenible.

### ¿Por qué este ejercicio?
- **Encapsulación**: Cálculos complejos en un solo lugar
- **Reutilización**: Usa la misma fórmula en múltiples lugares
- **Legibilidad**: El código que llama la función es más claro
- **Mantenibilidad**: Cambios en la fórmula solo en un lugar

### En el mundo real:
En un sistema de e-commerce, calcular descuentos, impuestos y envíos requiere fórmulas complejas. Funciones encapsulan estas fórmulas para reutilizarlas.`,
      keyPoints: [
        'Las funciones pueden contener múltiples operaciones',
        'Puedes usar módulos como math para funciones matemáticas',
        'Encapsula fórmulas complejas en funciones',
        'Hace el código más legible y mantenible'
      ],
      realWorldExample: 'En un sistema de facturación, una función calcular_total() encapsula: subtotal, descuentos, impuestos y envío. Sin funciones, este cálculo estaría repetido en múltiples lugares.'
    },
    theory: `# Funciones con Cálculos Complejos

## Encapsular Cálculos

Las funciones pueden contener múltiples operaciones:

\`\`\`python
def area_circulo(radio):
    return math.pi * radio ** 2
\`\`\`

## Múltiples Operaciones

Puedes combinar varias operaciones:

\`\`\`python
def calcular_total(precio, descuento, impuesto):
    subtotal = precio * (1 - descuento / 100)
    total = subtotal * (1 + impuesto / 100)
    return total
\`\`\`

## Usar Módulos

Puedes importar módulos para cálculos avanzados:

\`\`\`python
import math

def volumen_esfera(radio):
    return (4/3) * math.pi * radio ** 3
\`\`\`

## Ventajas

- **Legibilidad**: El código que llama es más claro
- **Mantenibilidad**: Cambios en un solo lugar
- **Reutilización**: Usa la misma fórmula múltiples veces
- **Testeo**: Fácil probar con diferentes valores`,
    tips: [
      'Puedes usar múltiples operaciones en una función',
      'Importa módulos como math para funciones matemáticas',
      'Encapsula fórmulas complejas en funciones',
      'Usa variables intermedias para cálculos complejos',
      'Comenta fórmulas matemáticas para claridad'
    ],
    advice: [
      'Divide cálculos complejos en pasos claros',
      'Usa nombres descriptivos para variables intermedias',
      'Comenta fórmulas matemáticas',
      'Prueba con diferentes valores para verificar'
    ]
  },
  {
    id: 7,
    title: 'Funciones con strings',
    description: 'Crea funciones que trabajan con cadenas de texto, formateando y procesando strings.',
    initialCode: `# Crea una función que formatea un nombre completo
def nombre_completo(nombre, apellido):
    # Devuelve "Nombre Apellido" formateado
    return 

# Crea una función que crea un saludo personalizado
def crear_saludo(nombre, hora):
    # Si hora < 12: "Buenos días"
    # Si hora < 18: "Buenas tardes"
    # Si no: "Buenas noches"
    if hora < 12:
        saludo = "Buenos días"
    elif hora < 18:
        saludo = "Buenas tardes"
    else:
        saludo = "Buenas noches"
    return f"{saludo}, {nombre}!"

# Prueba las funciones
print(nombre_completo("Juan", "Pérez"))
print(crear_saludo("María", 14))`,
    solution: `# Crea una función que formatea un nombre completo
def nombre_completo(nombre, apellido):
    # Devuelve "Nombre Apellido" formateado
    return f"{nombre} {apellido}"

# Crea una función que crea un saludo personalizado
def crear_saludo(nombre, hora):
    # Si hora < 12: "Buenos días"
    # Si hora < 18: "Buenas tardes"
    # Si no: "Buenas noches"
    if hora < 12:
        saludo = "Buenos días"
    elif hora < 18:
        saludo = "Buenas tardes"
    else:
        saludo = "Buenas noches"
    return f"{saludo}, {nombre}!"

# Prueba las funciones
print(nombre_completo("Juan", "Pérez"))
print(crear_saludo("María", 14))`,
    testCases: [],
    hints: [
      'Usa f-strings para formatear strings',
      'Puedes combinar strings con + o f-strings',
      'Las funciones pueden devolver strings formateados',
      'Puedes usar condicionales dentro de funciones'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué funciones con strings?',
      content: `Las funciones que trabajan con strings son muy comunes en programación. Te permiten formatear, procesar y manipular texto de forma reutilizable.

### ¿Por qué este ejercicio?
- **Formateo**: Funciones que formatean texto de forma consistente
- **Reutilización**: Mismo formato en múltiples lugares
- **Mantenibilidad**: Cambios en el formato solo en un lugar
- **Aplicación práctica**: Formateo de nombres, mensajes, etc.

### En el mundo real:
En un sistema de emails, una función formatear_mensaje() crea mensajes personalizados. Sin funciones, tendrías que repetir el código de formateo en cada lugar.`,
      keyPoints: [
        'Las funciones pueden devolver strings formateados',
        'Usa f-strings para interpolación de variables',
        'Puedes combinar strings con operadores',
        'Funciones de formateo hacen el código más consistente'
      ],
      realWorldExample: 'En un sistema de notificaciones, una función crear_notificacion() formatea mensajes personalizados. Sin funciones, cada notificación tendría código de formateo duplicado.'
    },
    theory: `# Funciones con Strings

## Formatear Strings

Las funciones pueden devolver strings formateados:

\`\`\`python
def nombre_completo(nombre, apellido):
    return f"{nombre} {apellido}"
\`\`\`

## F-strings

Usa f-strings para interpolación:

\`\`\`python
def mensaje_bienvenida(nombre, edad):
    return f"Bienvenido {nombre}, tienes {edad} años"
\`\`\`

## Combinar Strings

Puedes combinar strings de diferentes formas:

\`\`\`python
def crear_email(usuario, dominio):
    return usuario + "@" + dominio  # Con +
    # O con f-string:
    # return f"{usuario}@{dominio}"
\`\`\`

## Condicionales en Strings

Puedes usar condicionales para crear strings:

\`\`\`python
def saludo_tiempo(nombre, hora):
    if hora < 12:
        return f"Buenos días, {nombre}"
    else:
        return f"Buenas tardes, {nombre}"
\`\`\`

## Ventajas

- **Consistencia**: Mismo formato en todo el código
- **Reutilización**: Formatea strings en múltiples lugares
- **Mantenibilidad**: Cambios en un solo lugar`,
    tips: [
      'Usa f-strings para formatear strings',
      'Puedes combinar strings con + o f-strings',
      'Las funciones pueden devolver strings formateados',
      'Usa condicionales para crear strings dinámicos',
      'F-strings son más legibles que concatenación'
    ],
    advice: [
      'Usa f-strings en lugar de concatenación cuando sea posible',
      'Crea funciones de formateo para mantener consistencia',
      'Piensa en qué partes del string cambian',
      'Prueba con diferentes valores para verificar el formato'
    ]
  },
  {
    id: 8,
    title: 'Funciones con listas',
    description: 'Crea funciones que trabajan con listas, procesando y manipulando colecciones de datos.',
    initialCode: `# Crea una función que suma todos los números de una lista
def sumar_lista(numeros):
    # Suma todos los números de la lista
    total = 0
    for numero in numeros:
        # Completa aquí
    return total

# Crea una función que encuentra el número mayor
def encontrar_mayor(numeros):
    # Encuentra el número mayor de la lista
    mayor = numeros[0]
    for numero in numeros:
        # Completa aquí
    return mayor

# Prueba las funciones
lista1 = [1, 2, 3, 4, 5]
lista2 = [10, 5, 20, 15]
print("Suma:", sumar_lista(lista1))
print("Mayor:", encontrar_mayor(lista2))`,
    solution: `# Crea una función que suma todos los números de una lista
def sumar_lista(numeros):
    # Suma todos los números de la lista
    total = 0
    for numero in numeros:
        total += numero
    return total

# Crea una función que encuentra el número mayor
def encontrar_mayor(numeros):
    # Encuentra el número mayor de la lista
    mayor = numeros[0]
    for numero in numeros:
        if numero > mayor:
            mayor = numero
    return mayor

# Prueba las funciones
lista1 = [1, 2, 3, 4, 5]
lista2 = [10, 5, 20, 15]
print("Suma:", sumar_lista(lista1))
print("Mayor:", encontrar_mayor(lista2))`,
    testCases: [],
    hints: [
      'Usa un ciclo for para recorrer la lista',
      'Acumula valores en una variable',
      'Compara valores para encontrar el mayor',
      'Las funciones pueden recibir listas como parámetros'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué funciones con listas?',
      content: `Las funciones que procesan listas son fundamentales en programación. Te permiten manipular colecciones de datos de forma reutilizable y eficiente.

### ¿Por qué este ejercicio?
- **Procesamiento**: Funciones que procesan colecciones de datos
- **Reutilización**: Mismo procesamiento en múltiples listas
- **Eficiencia**: Código optimizado para trabajar con listas
- **Aplicación práctica**: Cálculos sobre datos, búsquedas, filtros

### En el mundo real:
En un sistema de ventas, una función calcular_total_ventas() procesa una lista de ventas. Sin funciones, tendrías que repetir el código de procesamiento cada vez.`,
      keyPoints: [
        'Las funciones pueden recibir listas como parámetros',
        'Usa ciclos for para recorrer listas',
        'Puedes acumular valores en variables',
        'Funciones de procesamiento hacen el código más eficiente'
      ],
      realWorldExample: 'En un sistema de inventario, una función calcular_stock_total() procesa una lista de productos. Sin funciones, este cálculo estaría repetido en múltiples lugares.'
    },
    theory: `# Funciones con Listas

## Recibir Listas como Parámetros

Las funciones pueden recibir listas:

\`\`\`python
def sumar_lista(numeros):
    total = 0
    for numero in numeros:
        total += numero
    return total
\`\`\`

## Procesar Listas

Puedes procesar listas con ciclos:

\`\`\`python
def encontrar_mayor(numeros):
    mayor = numeros[0]
    for numero in numeros:
        if numero > mayor:
            mayor = numero
    return mayor
\`\`\`

## Acumular Valores

Acumula valores en una variable:

\`\`\`python
def contar_pares(numeros):
    contador = 0
    for numero in numeros:
        if numero % 2 == 0:
            contador += 1
    return contador
\`\`\`

## Modificar Listas

Puedes modificar listas dentro de funciones:

\`\`\`python
def duplicar_lista(numeros):
    resultado = []
    for numero in numeros:
        resultado.append(numero * 2)
    return resultado
\`\`\`

## Ventajas

- **Eficiencia**: Procesa listas de forma optimizada
- **Reutilización**: Mismo procesamiento en múltiples listas
- **Legibilidad**: Código más claro y organizado`,
    tips: [
      'Las funciones pueden recibir listas como parámetros',
      'Usa ciclos for para recorrer listas',
      'Acumula valores en variables',
      'Puedes modificar o crear nuevas listas',
      'Usa índices cuando necesites la posición'
    ],
    advice: [
      'Piensa en qué operación necesitas hacer con cada elemento',
      'Usa variables acumuladoras para sumas, conteos, etc.',
      'Crea nuevas listas en lugar de modificar las originales cuando sea posible',
      'Prueba con listas vacías y con un solo elemento'
    ]
  },
  {
    id: 9,
    title: 'Funciones con diccionarios',
    description: 'Crea funciones que trabajan con diccionarios, accediendo y procesando datos estructurados.',
    initialCode: `# Crea una función que obtiene información de un producto
def obtener_info_producto(producto):
    # producto es un diccionario con 'nombre' y 'precio'
    # Devuelve un string formateado: "Producto: nombre, Precio: $precio"
    return 

# Crea una función que calcula el total de una compra
def calcular_total_compra(items):
    # items es una lista de diccionarios, cada uno con 'precio' y 'cantidad'
    # Calcula el total sumando precio * cantidad de cada item
    total = 0
    for item in items:
        # Completa aquí
    return total

# Prueba las funciones
producto = {"nombre": "Laptop", "precio": 999.99}
compra = [
    {"precio": 10, "cantidad": 2},
    {"precio": 5, "cantidad": 3}
]
print(obtener_info_producto(producto))
print("Total compra:", calcular_total_compra(compra))`,
    solution: `# Crea una función que obtiene información de un producto
def obtener_info_producto(producto):
    # producto es un diccionario con 'nombre' y 'precio'
    # Devuelve un string formateado: "Producto: nombre, Precio: $precio"
    return f"Producto: {producto['nombre']}, Precio: $" + str(producto['precio'])

# Crea una función que calcula el total de una compra
def calcular_total_compra(items):
    # items es una lista de diccionarios, cada uno con 'precio' y 'cantidad'
    # Calcula el total sumando precio * cantidad de cada item
    total = 0
    for item in items:
        total += item['precio'] * item['cantidad']
    return total

# Prueba las funciones
producto = {"nombre": "Laptop", "precio": 999.99}
compra = [
    {"precio": 10, "cantidad": 2},
    {"precio": 5, "cantidad": 3}
]
print(obtener_info_producto(producto))
print("Total compra:", calcular_total_compra(compra))`,
    testCases: [],
    hints: [
      'Accede a valores del diccionario con corchetes: diccionario["clave"]',
      'Puedes iterar sobre listas de diccionarios',
      'Combina acceso a diccionarios con cálculos',
      'Usa f-strings para formatear información'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué funciones con diccionarios?',
      content: `Las funciones que trabajan con diccionarios son esenciales para procesar datos estructurados. Te permiten acceder y manipular información de forma organizada.

### ¿Por qué este ejercicio?
- **Datos estructurados**: Funciones que procesan información organizada
- **Acceso eficiente**: Accede a datos por clave en lugar de posición
- **Aplicación práctica**: Procesamiento de datos JSON, APIs, configuraciones
- **Reutilización**: Mismo procesamiento en múltiples diccionarios

### En el mundo real:
En un sistema de e-commerce, una función procesar_pedido() trabaja con diccionarios que contienen información del pedido. Sin funciones, este procesamiento estaría repetido.`,
      keyPoints: [
        'Las funciones pueden recibir diccionarios como parámetros',
        'Accede a valores con diccionario["clave"]',
        'Puedes iterar sobre listas de diccionarios',
        'Combina acceso a diccionarios con cálculos y formateo'
      ],
      realWorldExample: 'En un sistema de usuarios, una función formatear_perfil() procesa un diccionario con información del usuario. Sin funciones, este formateo estaría repetido en múltiples lugares.'
    },
    theory: `# Funciones con Diccionarios

## Recibir Diccionarios

Las funciones pueden recibir diccionarios:

\`\`\`python
def obtener_info(producto):
    return f"{producto['nombre']}: $" + str(producto['precio'])
\`\`\`

## Acceder a Valores

Accede a valores con corchetes:

\`\`\`python
def calcular_total(item):
    return item['precio'] * item['cantidad']
\`\`\`

## Procesar Listas de Diccionarios

Puedes iterar sobre listas de diccionarios:

\`\`\`python
def calcular_total_compra(items):
    total = 0
    for item in items:
        total += item['precio'] * item['cantidad']
    return total
\`\`\`

## Combinar con Formateo

Combina acceso a diccionarios con formateo:

\`\`\`python
def formatear_producto(producto):
    return f"{producto['nombre']} - $" + str(producto['precio'])
\`\`\`

## Ventajas

- **Estructura**: Datos organizados por claves
- **Legibilidad**: Código más claro con nombres descriptivos
- **Flexibilidad**: Fácil agregar o quitar campos`,
    tips: [
      'Accede a valores con diccionario["clave"]',
      'Puedes iterar sobre listas de diccionarios',
      'Combina acceso a diccionarios con cálculos',
      'Usa f-strings para formatear información',
      'Verifica que las claves existan antes de acceder'
    ],
    advice: [
      'Usa nombres descriptivos para las claves',
      'Verifica que las claves existan antes de acceder',
      'Combina diccionarios con listas para datos complejos',
      'Prueba con diccionarios vacíos y con diferentes estructuras'
    ]
  },
  {
    id: 10,
    title: 'Funciones combinadas',
    description: 'Crea funciones que combinan múltiples conceptos: parámetros, return, listas y diccionarios.',
    initialCode: `# Crea una función que procesa una lista de productos
def procesar_productos(productos):
    # productos es una lista de diccionarios
    # Cada diccionario tiene 'nombre' y 'precio'
    # Devuelve una lista de strings formateados: "Producto: nombre - $precio"
    resultado = []
    for producto in productos:
        # Completa aquí
    return resultado

# Crea una función que encuentra el producto más caro
def producto_mas_caro(productos):
    # Encuentra el diccionario con el precio más alto
    # Devuelve el nombre del producto
    if not productos:
        return None
    mas_caro = productos[0]
    for producto in productos:
        # Completa aquí
    return mas_caro['nombre']

# Prueba las funciones
productos = [
    {"nombre": "Laptop", "precio": 999.99},
    {"nombre": "Mouse", "precio": 25.50},
    {"nombre": "Teclado", "precio": 75.00}
]
print(procesar_productos(productos))
print("Más caro:", producto_mas_caro(productos))`,
    solution: `# Crea una función que procesa una lista de productos
def procesar_productos(productos):
    # productos es una lista de diccionarios
    # Cada diccionario tiene 'nombre' y 'precio'
    # Devuelve una lista de strings formateados: "Producto: nombre - $precio"
    resultado = []
    for producto in productos:
        resultado.append(f"Producto: {producto['nombre']} - $" + str(producto['precio']))
    return resultado

# Crea una función que encuentra el producto más caro
def producto_mas_caro(productos):
    # Encuentra el diccionario con el precio más alto
    # Devuelve el nombre del producto
    if not productos:
        return None
    mas_caro = productos[0]
    for producto in productos:
        if producto['precio'] > mas_caro['precio']:
            mas_caro = producto
    return mas_caro['nombre']

# Prueba las funciones
productos = [
    {"nombre": "Laptop", "precio": 999.99},
    {"nombre": "Mouse", "precio": 25.50},
    {"nombre": "Teclado", "precio": 75.00}
]
print(procesar_productos(productos))
print("Más caro:", producto_mas_caro(productos))`,
    testCases: [],
    hints: [
      'Combina iteración sobre listas con acceso a diccionarios',
      'Puedes crear nuevas listas dentro de funciones',
      'Compara valores de diccionarios para encontrar el mayor',
      'Usa append() para agregar elementos a una lista'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué funciones combinadas?',
      content: `Las funciones combinadas integran múltiples conceptos, permitiéndote crear soluciones más complejas y realistas. Son la base de la programación práctica.

### ¿Por qué este ejercicio?
- **Integración**: Combina múltiples conceptos aprendidos
- **Aplicación práctica**: Funciones reales combinan varios conceptos
- **Complejidad gradual**: Construye sobre conocimientos previos
- **Preparación**: Te prepara para código más complejo

### En el mundo real:
En un sistema de inventario, una función generar_reporte() combina: iteración sobre listas, acceso a diccionarios, cálculos y formateo. Sin funciones, este código estaría disperso y difícil de mantener.`,
      keyPoints: [
        'Las funciones pueden combinar múltiples conceptos',
        'Itera sobre listas de diccionarios',
        'Crea nuevas estructuras de datos',
        'Combina procesamiento con formateo'
      ],
      realWorldExample: 'En un sistema de ventas, una función generar_factura() combina: procesar items, calcular totales, aplicar descuentos y formatear el resultado. Sin funciones, este código sería muy difícil de mantener.'
    },
    theory: `# Funciones Combinadas

## Integrar Conceptos

Las funciones pueden combinar múltiples conceptos:

\`\`\`python
def procesar_productos(productos):
    resultado = []
    for producto in productos:
        resultado.append(f"{producto['nombre']}: $" + str(producto['precio']))
    return resultado
\`\`\`

## Iterar y Procesar

Combina iteración con procesamiento:

\`\`\`python
def encontrar_mayor(productos):
    mayor = productos[0]
    for producto in productos:
        if producto['precio'] > mayor['precio']:
            mayor = producto
    return mayor
\`\`\`

## Crear Nuevas Estructuras

Puedes crear nuevas listas o diccionarios:

\`\`\`python
def resumir_ventas(ventas):
    total = 0
    for venta in ventas:
        total += venta['monto']
    return {"total": total, "cantidad": len(ventas)}
\`\`\`

## Combinar Operaciones

Combina múltiples operaciones:

\`\`\`python
def procesar_pedido(items):
    subtotal = 0
    for item in items:
        subtotal += item['precio'] * item['cantidad']
    impuesto = subtotal * 0.21
    return {"subtotal": subtotal, "impuesto": impuesto, "total": subtotal + impuesto}
\`\`\`

## Ventajas

- **Complejidad**: Maneja situaciones reales
- **Organización**: Código estructurado y claro
- **Reutilización**: Funciones complejas reutilizables`,
    tips: [
      'Combina iteración con acceso a diccionarios',
      'Crea nuevas estructuras de datos cuando sea necesario',
      'Divide funciones complejas en pasos claros',
      'Usa variables intermedias para claridad',
      'Prueba con diferentes estructuras de datos'
    ],
    advice: [
      'Divide funciones complejas en pasos claros',
      'Usa variables intermedias para hacer el código más legible',
      'Piensa en qué estructura de datos necesitas crear',
      'Prueba con listas vacías y con diferentes estructuras',
      'Comenta secciones complejas para claridad'
    ]
  }
];

